import React, { useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { IChannel, ParticipationEventState } from "@Interfaces";
import { MainSelector, ParticipationActions, useAppDispatch } from "@Store";
import useParticipationPolling from "@Hooks/useParticipationPolling";
import useParticipationUsageTracking from "@Hooks/useParticipationUsageTracking";
import { ParticipationHeader } from "./ParticipationHeader";
import { ParticipationWidgets } from "./ParticipationWidgets";
import { PARTICIPATION_MUTED_TEXT } from "./ParticipationWidgets/participationStyles";

interface ParticipationTabProps {
    channel?: IChannel;
}

export const ParticipationTab: React.FC<ParticipationTabProps> = ({
    channel,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const channelId = channel?._id;
    const storeChannelId = MainSelector(
        (state) => state.participation.channelId
    );
    // Until the fetch for this channel starts, the store may still hold another channel.
    const isCurrentChannel = Boolean(channelId) && storeChannelId === channelId;
    const storedSummary = MainSelector((state) => state.participation.data);
    const storedLoadError = MainSelector(
        (state) => state.participation.loadError
    );
    const summary = isCurrentChannel ? storedSummary : null;
    const loadError = isCurrentChannel ? storedLoadError : null;
    const isLoading =
        MainSelector((state) => state.participation.isLoading) ||
        !isCurrentChannel;

    const refetch = useCallback(() => {
        if (channelId) {
            dispatch(ParticipationActions.fetchParticipationSummary(channelId));
        }
    }, [channelId, dispatch]);

    useEffect(refetch, [refetch]);

    // Closing elsewhere (another tab, a co-admin, the parent) only shows up as a 403.
    const state: ParticipationEventState =
        channel?.closed || loadError === "closed"
            ? "closed"
            : summary && summary.respondents > 0
            ? "collecting"
            : "waiting";

    // Poll only once this channel has numbers; a parent event never gets any.
    useParticipationPolling(
        summary && loadError !== "parent" ? state : undefined,
        refetch
    );

    useParticipationUsageTracking(
        channelId,
        state,
        isLoading,
        Boolean(summary) && !loadError
    );

    return (
        <Box
            sx={{
                fontFamily:
                    '"Inter Display", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                display: "flex",
                flexDirection: "column",
                gap: 2,
                // Room below the last cards so the page doesn't end flush against them.
                pb: 5,
            }}
        >
            {(summary || loadError === "closed") && (
                <ParticipationHeader state={state} />
            )}

            {isLoading && !summary && !loadError && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {loadError && loadError !== "generic" && !summary && (
                <Typography
                    sx={{
                        color: PARTICIPATION_MUTED_TEXT,
                        fontSize: "1.125rem",
                    }}
                >
                    {loadError === "closed"
                        ? t("pages.participation.loadErrorClosed")
                        : t("pages.participation.loadErrorParent")}
                </Typography>
            )}

            {loadError === "generic" && !summary && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 4,
                    }}
                >
                    <Typography
                        sx={{
                            color: PARTICIPATION_MUTED_TEXT,
                            fontSize: "1.125rem",
                        }}
                    >
                        {t("pages.participation.loadError")}
                    </Typography>
                    <Button onClick={refetch}>
                        {t("pages.participation.retry")}
                    </Button>
                </Box>
            )}

            {summary && channelId && (
                <ParticipationWidgets channelId={channelId} summary={summary} />
            )}
        </Box>
    );
};
